import { Component, NgZone, OnInit } from '@angular/core';
import { Select } from '@ngxs/store';
import { ToastService } from '@services/common/toast.service';
import { UserHttpService } from '@services/datasources/user-http.service';
import { OrganizationState } from '@state/organization.state';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { combineLatest, Observable } from 'rxjs';
import { SecurityRoleDto } from 'app/@types/dto/security-role-dto.model';
import { userRoles } from 'app/@types/dto/userRoles.model';
import { UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';

@Component({
    selector: 'app-settings-form-users-managment',
    templateUrl: './settings-form-users-managment.component.html',
    standalone: false,
})
export class SettingsFormUsersManagmentComponent implements OnInit {
    @Select(OrganizationState.getOrganization) organization$!: Observable<OrganizationDto>;
    @Select(OrganizationState.getOrganizationPricingPlan) pricingPlan$!: Observable<
        OrganizationPricingPlanDto | undefined
    >;
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims>;

    selectedUser: UserInfoDto | undefined = undefined;
    // The organization must keep a Main user: the only one cannot change their role (the API refuses it too).
    selectedUserIsLastMainUser = false;
    addingEditingMode: boolean = false;
    constructor(
        public toastService: ToastService,
        private readonly ngZone: NgZone,
        private readonly userService: UserHttpService,
    ) {}
    usersList: UserInfoDto[] = [];
    orgId: string = '';
    canAdd = false;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public pricingPlan: OrganizationPricingPlanDto;
    public isLoading: boolean = true;
    public isOrgOwner: boolean = false;

    ngOnInit() {
        this.organization$.subscribe((org) => {
            if (org?.uid) {
                this.orgId = org.uid;
                combineLatest([
                    this.userService.getUserByOrganization(org.uid),
                    this.pricingPlan$,
                    this.userClaims$,
                ]).subscribe(([users, pricingPlan, claims]) => {
                    this.usersList = users;
                    this.isOrgOwner = claims.tasks.some(
                        (task) => task.name === 'orguser' && task.organization_id === org.uid,
                    );
                    if (pricingPlan) {
                        this.pricingPlan = pricingPlan;
                        this.canAdd = +pricingPlan.licenseDoc.max_users > users.length && this.isOrgOwner;
                        this.isLoading = false;
                    }
                });
            }
        });
    }

    getRoleNameLabel(roles: SecurityRoleDto[]): string {
        const role = roles.find((r) => r.organization_uid === this.orgId);
        if (!role) {
            return '--';
        }
        switch (role.role_name) {
            case userRoles.main_user:
                return 'settings.users.roles.mainUser';
            case userRoles.viewer:
                return 'settings.users.roles.viewer';
            default:
                return 'settings.users.roles.editor';
        }
    }

    getRoleLastConnexion(roles: SecurityRoleDto[]) {
        const role = roles.find((r) => r.organization_uid === this.orgId);
        if (role?.last_connected) {
            const date = new Date(role.last_connected);
            const formatter = new Intl.DateTimeFormat('fr-FR');

            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');

            return `${formatter.format(new Date(role.last_connected))}  ${hours}:${minutes}:${seconds}`;
        }

        return '--';
    }

    addEditUser(user: UserInfoDto | undefined = undefined) {
        this.selectedUser = user;
        this.selectedUserIsLastMainUser =
            !!user && this.isMainUser(user) && this.usersList.filter((u) => this.isMainUser(u)).length <= 1;
        this.addingEditingMode = true;
    }

    private isMainUser(user: UserInfoDto): boolean {
        return user.roles.some((r) => r.organization_uid === this.orgId && r.role_name === userRoles.main_user);
    }

    oncloseEditUser(event?: { translateKey: string; type: string }) {
        this.addingEditingMode = false;
        // Cancelled (no event): nothing changed, keep the current list — no reload, no skeleton.
        if (!event) {
            this.isLoading = false;
            return;
        }
        this.ngZone.run(() => {
            if (event.type === 'success') {
                this.toastService.showSuccess({
                    details: event.translateKey,
                });
            } else {
                this.toastService.showError({
                    details: event.translateKey,
                });
            }
            if (!this.orgId) {
                this.isLoading = false;
                return;
            }
            // Keep the skeleton up until the refreshed list actually lands, so the user
            // never sees the stale list flash before it updates.
            this.isLoading = true;
            this.userService.getUserByOrganization(this.orgId).subscribe({
                next: (users) => {
                    this.usersList = users;
                    this.canAdd = +this.pricingPlan.licenseDoc.max_users > users.length && this.isOrgOwner;
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                },
            });
        });
    }
}
